/**
 * useRates — load the actuarial rate table once per session.
 *
 * The rates table is read-only and refreshed at most annually by
 * Underwriting, so a module-level singleton cache is the right shape:
 *   - First hook caller triggers the fetch.
 *   - All subsequent callers share the same in-flight promise and
 *     then the resolved value.
 *   - No retries or refetch-on-focus — if the network blip leaves us
 *     with `error`, the user can refresh the page (premium chain just
 *     renders "—" in the meantime).
 *
 * The state machine mirrors `use-litigation`:
 *   { status: "loading" } → { status: "ready", data } | { status: "error", error }
 */
import { useEffect, useState } from "react";
import { getRates } from "../api";
import type { RateRow } from "../domain/pricing";

export type RatesQueryState =
    | { status: "loading" }
    | { status: "error"; error: Error }
    | { status: "ready"; data: RateRow[] };

/** Module-level singleton — resolved promise once the first fetch
 *  completes. `null` until the first call. */
let inflight: Promise<RateRow[]> | null = null;

function loadRatesOnce(): Promise<RateRow[]> {
    if (inflight) return inflight;
    inflight = getRates()
        .then((res) => res.rates)
        .catch((error) => {
            // Reset on failure so a subsequent caller (re-mount, page
            // navigation) can retry — without this, the singleton
            // locks every consumer into the error state forever.
            inflight = null;
            throw error;
        });
    return inflight;
}

export function useRates(): RatesQueryState {
    const [state, setState] = useState<RatesQueryState>({ status: "loading" });

    useEffect(() => {
        let cancelled = false;
        loadRatesOnce()
            .then((data) => {
                if (cancelled) return;
                setState({ status: "ready", data });
            })
            .catch((error: unknown) => {
                if (cancelled) return;
                setState({
                    status: "error",
                    error: error instanceof Error ? error : new Error("Failed to load rates"),
                });
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return state;
}
